// messageCenter/attachmentShrink.js — images are made small enough for the server before the upload (decision D-18):
// longest side at most 2560 px, re-encoded as WebP (JPEG where the browser cannot write WebP). An image that already
// fits stays untouched, so small screenshots keep their sharp text.

export const MAX_SIDE = 2560;
const QUALITIES = [0.85, 0.75, 0.6];
const SAFETY = 0.9;   // stay below the server limit: multipart overhead, rounding

/** Types the server accepts (MessageCenterUpload::TYPES). */
export const ACCEPTED_TYPES = Object.freeze(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf']);
/** Types the browser re-encodes; GIF stays as it is (may be animated), PDF is not an image. */
const SHRINKABLE = ['image/png', 'image/jpeg', 'image/webp'];

/** null = keep the original; else the target size. Pure. */
export function shrinkPlan({ width, height, bytes }, fileLimit) {
    const longest = Math.max(width, height);
    if (longest <= MAX_SIDE && bytes <= fileLimit * SAFETY) { return null; }
    const scale = Math.min(1, MAX_SIDE / longest);
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** The file, or a smaller copy of it that fits fileLimit; throws if even the smallest try is too large. */
export async function shrinkImage(file, fileLimit) {
    if (!SHRINKABLE.includes(file.type)) { return file; }
    const bitmap = await createImageBitmap(file);
    try {
        const plan = shrinkPlan({ width: bitmap.width, height: bitmap.height, bytes: file.size }, fileLimit);
        if (!plan) { return file; }
        const canvas = document.createElement('canvas');
        canvas.width = plan.width;
        canvas.height = plan.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0, plan.width, plan.height);
        for (const quality of QUALITIES) {
            let blob = await toBlob(canvas, 'image/webp', quality);
            if (blob.type !== 'image/webp') { blob = await toBlob(canvas, 'image/jpeg', quality); }   // no WebP encoder
            if (blob.size <= fileLimit * SAFETY) {
                const extension = blob.type === 'image/webp' ? '.webp' : '.jpg';
                return new File([blob], file.name.replace(/\.[^.]*$/, '') + extension, { type: blob.type });
            }
        }
        throw new Error('still too large after shrinking: ' + file.name);
    } finally {
        bitmap.close?.();
    }
}

function toBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob gave nothing'))), type, quality);
    });
}
